import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import type {
  DehnungsHExerciseConfig,
  DehnungsHRunSettings,
  KurzeLangeVokaleExerciseConfig,
  KurzeLangeVokaleRunSettings,
} from "@calega/content";
import { blitzlesenSyllables, type SpellingChoiceRun, type SpellingChoiceRunItem } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type ExerciseType = "dehnungs-h" | "kurze-lange-vokale";
type Config = DehnungsHExerciseConfig | KurzeLangeVokaleExerciseConfig;
type Settings = DehnungsHRunSettings | KurzeLangeVokaleRunSettings;
type Phase = "running" | "feedback" | "complete";

interface Answer {
  itemId: string;
  optionId: "correct" | "incorrect";
  correct: boolean;
}

interface State {
  result?: { score: number; maxScore: number };
  runId: string;
  run: SpellingChoiceRun;
  index: number;
  phase: Phase;
  endsAt: number | null;
  answers: Answer[];
}

function VowelWord({ item, text, settings }: { item: SpellingChoiceRunItem; text: string; settings: Settings }) {
  const syllables = "syllableColors" in settings && settings.syllableColors ? blitzlesenSyllables(text) : [text];
  const syllableByIndex: number[] = [];
  let offset = 0;
  syllables.forEach((syllable, syllableIndex) => {
    for (let index = 0; index < Array.from(syllable).length; index += 1) syllableByIndex[offset + index] = syllableIndex;
    offset += Array.from(syllable).length;
  });
  const highlight = "shortVowelHighlight" in settings && settings.shortVowelHighlight && item.role === "target_rule_word"
    ? "short"
    : "longVowelHighlight" in settings && settings.longVowelHighlight && item.role === "contrast_word"
      ? "long"
      : null;
  return <span className="spelling-word">{Array.from(text).map((letter, index) => <span className={`spelling-letter ${"syllableColors" in settings && settings.syllableColors ? `color-${(syllableByIndex[index] ?? 0) % 2}` : ""} ${highlight && index === item.vowelIndex ? `vowel-${highlight}` : ""}`} key={`${letter}-${index}`}>{letter}</span>)}</span>;
}

export function SpellingChoice({ account, config, pageId, type }: { account: Account; config: Config; pageId: string; type: ExerciseType }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const syncFollower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || syncFollower;
  const vowelConfig = type === "kurze-lange-vokale" ? config as KurzeLangeVokaleExerciseConfig : null;
  const [mode, setMode] = useState<"count" | "time">(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [categories, setCategories] = useState<string[]>(() => config.defaultCategories?.length ? config.defaultCategories : config.categories.map(({ id }) => id));
  const [syllableColors, setSyllableColors] = useState(vowelConfig?.syllableColorsDefault ?? false);
  const [shortVowelHighlight, setShortVowelHighlight] = useState(vowelConfig?.shortVowelHighlightDefault ?? false);
  const [longVowelHighlight, setLongVowelHighlight] = useState(vowelConfig?.longVowelHighlightDefault ?? false);
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, setClockTick] = useState(0);

  const labels = type === "dehnungs-h"
    ? { title: "Dehnungs-h", prompt: "Welche Schreibweise ist richtig?", start: "Dehnungs-h starten" }
    : { title: "Kurze oder lange Vokale", prompt: "Markiere die richtige Schreibweise.", start: "Vokal-Übung starten" };
  const score = useMemo(() => (exercise?.answers ?? []).filter((answer) => answer.correct).length, [exercise?.answers]);

  function publish(next: State) {
    realtime.publishExercise({ exerciseType: type, pageId, state: next });
  }

  function publishReset() {
    realtime.publishExercise({ exerciseType: type, pageId, state: null });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: State | null } | null;
    if (synced?.exerciseType === type && synced.pageId === pageId && synced.state === null) {
      setExercise(null);
      return;
    }
    if (synced?.exerciseType === type && synced.pageId === pageId && synced.state) setExercise(synced.state);
  }, [realtime.state.exercise, pageId, type]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => setClockTick((value) => value + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const remaining = Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow));
    const timer = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), remaining + 20);
    return () => clearExerciseTimeout(timer);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  function currentSettings(): Settings {
    const basic = { mode, count, durationSeconds, categories };
    return type === "dehnungs-h"
      ? basic
      : { ...basic, syllableColors, shortVowelHighlight, longVowelHighlight };
  }

  function setFromSettings(settings: Settings) {
    setMode(settings.mode);
    setCount(settings.count);
    setDurationSeconds(settings.durationSeconds);
    setCategories(settings.categories);
    if ("syllableColors" in settings) {
      setSyllableColors(settings.syllableColors);
      setShortVowelHighlight(settings.shortVowelHighlight);
      setLongVowelHighlight(settings.longVowelHighlight);
    }
  }

  async function start(settings = currentSettings()) {
    if (busy || !canControl || settings.categories.length === 0) return;
    setBusy(true);
    setMessage("Durchlauf wird vorbereitet …");
    try {
      const result = await startExerciseRun<{ runId: string; exercise: SpellingChoiceRun }>({ contentPageId: pageId, settings });
      if (!result.exercise.items.length) throw new Error("Für diese Auswahl sind keine passenden Wörter vorhanden.");
      const next: State = {
        runId: result.runId,
        run: result.exercise,
        index: 0,
        phase: "running",
        endsAt: result.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + result.exercise.settings.durationSeconds * 1_000 : null,
        answers: [],
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function abortCurrentRun() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abortCurrentRun();
      setFromSettings(exercise.run.settings);
      setExercise(null);
      setMessage("");
      publishReset();
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const settings = exercise.run.settings;
    setBusy(true);
    try {
      await abortCurrentRun();
    } finally {
      setBusy(false);
    }
    await start(settings);
  }

  async function answer(optionId: "correct" | "incorrect") {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type, itemId: item.id, optionId });
      const next: State = {
        ...exercise,
        phase: "feedback",
        answers: [...exercise.answers, { itemId: item.id, optionId, correct: optionId === "correct" }],
      };
      setExercise(next);
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) {
      await finish();
      return;
    }
    const nextState = { ...exercise, index: exercise.index + 1, phase: "running" as const };
    setExercise(nextState);
    publish(nextState);
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }
  async function closeCompletedRun(repeat: boolean) { if (!exercise || exercise.phase !== "complete") return; if (repeat) await remix(); else await backToSettings(); }

  function toggleCategory(category: string) {
    setCategories((current) => current.includes(category) ? current.filter((id) => id !== category) : [...current, category]);
  }

  if (syncFollower && !exercise) return <ExerciseWaitingCard className="spelling-choice-exercise" />;

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (!exercise) {
    const countOptions = type === "dehnungs-h" ? [5, 10, 15, 20] : [5, 10, 15, 20, 25, 30];
    return <section className="spelling-choice-exercise">
      <p className="quiz-instructions learning-text">{config.instructions}</p>
      <div className="spelling-choice-settings">
        <fieldset><legend>Durchlauf</legend><div className="blitz-choice-row">
          <label className="checkbox-label"><input type="radio" name={`${pageId}-${type}-modus`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label>
          <label className="checkbox-label"><input type="radio" name={`${pageId}-${type}-modus`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label>
        </div></fieldset>
        {mode === "count" ? <label>Aufgabenanzahl<select value={count} onChange={(event) => setCount(Number(event.target.value))}>{countOptions.map((value) => <option value={value} key={value}>{value}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}
        <fieldset><legend>{type === "dehnungs-h" ? "Muster" : "Doppelkonsonanz"}</legend><div className="blitz-choice-row">
          {config.categories.map((category) => <label className="checkbox-label" key={category.id}><input type="checkbox" checked={categories.includes(category.id)} onChange={() => toggleCategory(category.id)} />{category.label}</label>)}
        </div></fieldset>
        {type === "kurze-lange-vokale" && <fieldset><legend>Optionale Hilfen</legend><div className="blitz-choice-row">
          <label className="checkbox-label"><input type="checkbox" checked={syllableColors} onChange={(event) => setSyllableColors(event.target.checked)} />Silben einfärben</label>
          <label className="checkbox-label"><input type="checkbox" checked={shortVowelHighlight} onChange={(event) => setShortVowelHighlight(event.target.checked)} />Kurzvokal markieren</label>
          <label className="checkbox-label"><input type="checkbox" checked={longVowelHighlight} onChange={(event) => setLongVowelHighlight(event.target.checked)} />Langvokal markieren</label>
        </div></fieldset>}
      </div>
      <button className="primary" disabled={busy || !canControl || categories.length === 0} onClick={() => start().catch((error) => setMessage(error.message))}>{labels.start}</button>
      {!categories.length && <div className="alert info">Bitte mindestens eine Kategorie auswählen.</div>}
      {message && <div className="alert info">{message}</div>}
    </section>;
  }

  const item = exercise.run.items[exercise.index];
  const lastAnswer = exercise.answers.at(-1);
  const timeRemaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const completed = exercise.answers.length;
  return <section className="spelling-choice-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu>
      <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
      <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
    </ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${completed} beantwortet · ${timeRemaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <div className="reime-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? completed} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div> : item && <>
      <div className="spelling-choice-card"><h2 className="learning-text">{labels.prompt}</h2><div className="spelling-choice-options">
        {item.options.map((option) => {
          const selected = lastAnswer?.itemId === item.id && lastAnswer.optionId === option.id;
          const correct = option.id === "correct";
          return <button className={`spelling-choice-option learning-text ${exercise.phase === "feedback" && correct ? "is-correct" : ""} ${exercise.phase === "feedback" && selected && !correct ? "is-wrong" : ""}`} disabled={exercise.phase !== "running" || busy || !canAnswer} key={option.id} onClick={() => answer(option.id).catch((error) => setMessage(error.message))}>
            {type === "kurze-lange-vokale" ? <VowelWord item={item} text={option.text} settings={exercise.run.settings} /> : option.text}
          </button>;
        })}
      </div></div>
      {exercise.phase === "feedback" && lastAnswer && <div className={`answer-feedback learning-text ${lastAnswer.correct ? "correct" : "wrong"}`}>{lastAnswer.correct ? "Richtig." : `Nicht ganz. Richtig ist: ${item.correct}.`} {item.hint}</div>}
      {exercise.phase === "feedback" && canControl && <div className="reime-actions"><button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{(exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length ? "Auswertung ansehen" : "Nächstes Wort"}</button></div>}
    </>}
    {canControl && exercise.phase === "complete" && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
    {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
